import React, { useState, useEffect } from "react";
import axios from "axios";
import { LogIn, LogOut, Shield, RefreshCw, Download, Users, Briefcase } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const urgencyBadge = {
  emergencial: "bg-[#B91C1C] text-white",
  programada: "bg-[#0F2A44] text-white",
  normal: "bg-[#6B7785] text-white",
};

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("hse_admin_token") || "");
  const [user, setUser] = useState({ username: "", password: "" });
  const [leads, setLeads] = useState([]);
  const [careers, setCareers] = useState([]);
  const [tab, setTab] = useState("leads");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, user);
      localStorage.setItem("hse_admin_token", data.access_token);
      setToken(data.access_token);
    } catch (err) {
      setError(err?.response?.data?.detail || "Credenciais inválidas");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("hse_admin_token");
    setToken("");
    setLeads([]);
    setCareers([]);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [l, c] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/careers`, { headers }),
      ]);
      setLeads(l.data);
      setCareers(c.data);
    } catch (err) {
      if (err?.response?.status === 401) logout();
    } finally {
      setLoading(false);
    }
  };

  const downloadCV = async (id, filename) => {
    try {
      const res = await axios.get(`${API}/admin/careers/${id}/cv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erro ao baixar currículo.");
    }
  };

  useEffect(() => {
    if (token) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0F2A44] flex items-center justify-center px-4">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-md bg-white p-8 border border-[#E4E7EB]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#0F2A44] flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <div>
              <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-[#2FA36B] font-bold">Painel</p>
              <h1 className="font-condensed font-bold uppercase text-2xl text-[#0F2A44]">HSE Admin</h1>
            </div>
          </div>
          <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-[#6B7785] font-bold mb-2">Usuário</label>
          <input
            data-testid="admin-username"
            className="w-full border border-[#E4E7EB] px-4 py-3 mb-4 text-[#0F2A44] outline-none focus:border-[#2FA36B]"
            value={user.username}
            onChange={(e) => setUser({ ...user, username: e.target.value })}
            autoComplete="username"
            required
          />
          <label className="block font-condensed uppercase text-[10px] tracking-[0.3em] text-[#6B7785] font-bold mb-2">Senha</label>
          <input
            data-testid="admin-password"
            type="password"
            className="w-full border border-[#E4E7EB] px-4 py-3 mb-4 text-[#0F2A44] outline-none focus:border-[#2FA36B]"
            value={user.password}
            onChange={(e) => setUser({ ...user, password: e.target.value })}
            autoComplete="current-password"
            required
          />
          {error && <p data-testid="admin-error" className="text-[#B91C1C] font-body text-sm mb-3">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full inline-flex items-center justify-center gap-2 font-condensed uppercase text-sm tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#268C5A] text-white px-5 py-4 transition-colors disabled:opacity-60">
            <LogIn size={16} /> {loading ? "Entrando..." : "Entrar"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-[#0F2A44] text-white px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={20} />
          <div>
            <p className="font-condensed uppercase text-[10px] tracking-[0.3em] text-[#2FA36B] font-bold">Painel</p>
            <h1 className="font-condensed font-bold uppercase text-xl">HSE Admin</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="admin-refresh" onClick={fetchAll} className="inline-flex items-center gap-2 font-condensed uppercase text-xs tracking-[0.2em] font-bold border border-white/20 hover:border-white px-4 py-2">
            <RefreshCw size={14} /> Atualizar
          </button>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 font-condensed uppercase text-xs tracking-[0.2em] font-bold bg-[#B91C1C] hover:bg-[#DC2626] px-4 py-2">
            <LogOut size={14} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6 border-b border-[#E4E7EB]">
          <button
            data-testid="tab-leads"
            onClick={() => setTab("leads")}
            className={`font-condensed uppercase text-sm tracking-[0.2em] font-bold px-5 py-3 border-b-2 flex items-center gap-2 ${tab === "leads" ? "text-[#0F2A44] border-[#2FA36B]" : "text-[#6B7785] border-transparent hover:text-[#0F2A44]"}`}
          >
            <Users size={16} /> Leads ({leads.length})
          </button>
          <button
            data-testid="tab-careers"
            onClick={() => setTab("careers")}
            className={`font-condensed uppercase text-sm tracking-[0.2em] font-bold px-5 py-3 border-b-2 flex items-center gap-2 ${tab === "careers" ? "text-[#0F2A44] border-[#2FA36B]" : "text-[#6B7785] border-transparent hover:text-[#0F2A44]"}`}
          >
            <Briefcase size={16} /> Currículos ({careers.length})
          </button>
        </div>

        {tab === "leads" && (
          <div className="grid gap-4">
            {leads.length === 0 && !loading && (
              <div className="bg-white border border-[#E4E7EB] p-10 text-center text-[#6B7785] font-body">
                Nenhum lead ainda. Assim que uma solicitação chegar pelo site, aparecerá aqui.
              </div>
            )}
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-white border border-[#E4E7EB] p-5 grid md:grid-cols-6 gap-4 items-start">
                <div className="md:col-span-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`font-condensed uppercase text-[10px] tracking-[0.2em] font-bold px-2 py-1 ${urgencyBadge[l.urgencia] || urgencyBadge.normal}`}>
                      {l.urgencia}
                    </span>
                    <span className="font-body text-xs text-[#6B7785]">
                      {new Date(l.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                  <p className="font-condensed font-bold text-[#0F2A44] text-lg leading-tight">{l.nome}</p>
                  {l.empresa && <p className="font-body text-sm text-[#6B7785]">{l.empresa}</p>}
                </div>
                <div className="md:col-span-2 font-body text-sm text-[#0F2A44] space-y-1">
                  <p>📞 {l.telefone}</p>
                  <p>✉️ {l.email}</p>
                  {l.cidade_estado && <p>📍 {l.cidade_estado}</p>}
                  {l.servico_interesse && <p className="text-[#2FA36B] font-semibold">🔧 {l.servico_interesse}</p>}
                </div>
                <div className="md:col-span-2 font-body text-sm text-[#0F2A44] leading-relaxed">
                  {l.descricao}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "careers" && (
          <div className="grid gap-4">
            {careers.length === 0 && !loading && (
              <div className="bg-white border border-[#E4E7EB] p-10 text-center text-[#6B7785] font-body">
                Nenhum currículo recebido ainda.
              </div>
            )}
            {careers.map((c) => (
              <div key={c.id} data-testid={`career-${c.id}`} className="bg-white border border-[#E4E7EB] p-5 grid md:grid-cols-6 gap-4 items-start">
                <div className="md:col-span-2">
                  <p className="font-condensed uppercase text-[10px] tracking-[0.2em] text-[#2FA36B] font-bold">
                    {c.area_interesse}
                  </p>
                  <p className="font-condensed font-bold text-[#0F2A44] text-lg leading-tight mt-1">{c.nome}</p>
                  <p className="font-body text-xs text-[#6B7785] mt-1">
                    {new Date(c.created_at).toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="md:col-span-2 font-body text-sm text-[#0F2A44] space-y-1">
                  <p>📞 {c.telefone}</p>
                  <p>✉️ {c.email}</p>
                  {c.cidade_estado && <p>📍 {c.cidade_estado}</p>}
                </div>
                <div className="md:col-span-2 flex flex-col gap-3">
                  {c.mensagem && <p className="font-body text-xs text-[#6B7785] leading-relaxed line-clamp-3">{c.mensagem}</p>}
                  <button
                    onClick={() => downloadCV(c.id, c.curriculo_filename)}
                    data-testid={`download-cv-${c.id}`}
                    className="inline-flex items-center justify-center gap-2 font-condensed uppercase text-[11px] tracking-[0.2em] font-bold bg-[#2FA36B] hover:bg-[#268C5A] text-white px-4 py-2.5 transition-colors"
                  >
                    <Download size={14} /> Baixar CV ({(c.curriculo_size / 1024).toFixed(0)} KB)
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
